/* ==========================================================================
   LAUZON — story/specs split
   Product descriptions arrive from the sync pipeline as
   <div class="lz-desc"> + optional <div class="lz-specs"> (see
   product-sync/README.md). This moves lz-specs out of the story column into
   the spec column and folds each spec group (a <p> led by bold text) into a
   <details> accordion matching the prototype. A Liquid-rendered metafield
   spec sheet, when present, already fills the column and wins.
   ========================================================================== */

(function () {
  'use strict';

  // Descriptions authored in Lightspeed cannot carry <div class="lz-specs">:
  // Lightspeed strips div/span/class server-side, in the API as well as the
  // eCom editor (verified against Item 9904, 2026-08-27). <p>, <strong> and
  // <br> survive. So a plain marker paragraph — "SPECIFICATIONS" or "SPECS" —
  // is the fallback boundary, and everything after it is spec content.
  // lz-specs still wins wherever it exists, which keeps the 851 migrated
  // descriptions rendering exactly as before.
  var SPEC_MARKER = /^spec(ification)?s?\s*:?\s*$/i;

  function specsFromMarker(body) {
    var nodes = Array.prototype.slice.call(body.children);
    var at = -1;
    for (var i = 0; i < nodes.length; i++) {
      if (SPEC_MARKER.test(nodes[i].textContent.trim())) { at = i; break; }
    }
    if (at === -1) return null;

    var box = document.createElement('div');
    box.className = 'lz-specs';
    nodes.slice(at + 1).forEach(function (node) { box.appendChild(node); });
    nodes[at].remove();
    return box.children.length ? box : null;
  }

  function isBold(node) {
    return !!node && node.nodeType === 1 && (node.tagName === 'B' || node.tagName === 'STRONG');
  }

  function isBlank(node) {
    return node.nodeType === 3 && !node.textContent.trim();
  }

  function firstContent(parent) {
    for (var n = parent.firstChild; n; n = n.nextSibling) {
      if (!isBlank(n)) return n;
    }
    return null;
  }

  function nextContent(node) {
    for (var n = node.nextSibling; n; n = n.nextSibling) {
      if (!isBlank(n)) return n;
    }
    return null;
  }

  // Drop the blank line a removed heading leaves behind: leading whitespace
  // and <br>s, including ones that open the next bold run ("<strong><br>Inputs:").
  function trimLeadingBreaks(parent) {
    var n = parent.firstChild;
    while (n) {
      if (isBlank(n) || n.nodeName === 'BR') {
        var gone = n; n = n.nextSibling; gone.remove();
      } else if (isBold(n)) {
        trimLeadingBreaks(n);
        if (n.textContent.trim()) return;
        var empty = n; n = n.nextSibling; empty.remove();
      } else {
        return;
      }
    }
  }

  // A group heading is a bold run alone on its first line: "BODY<br>",
  // "Electronics:" followed by the next bold label, or a heading glued to the
  // first label inside one bold run ("<strong>Tubes<br>Power Tubes:</strong>",
  // which is how Fender's own spec copy arrives). textContent ignores <br>, so
  // reading the run as one string is what produced "TubesPower Tubes".
  // Returns the heading text with the heading removed from the block, or null.
  function takeHeading(block) {
    var lead = firstContent(block);
    if (!isBold(lead)) return null;

    var head = '';
    var cut = null;
    for (var n = lead.firstChild; n; n = n.nextSibling) {
      if (n.nodeName === 'BR') { cut = n; break; }
      head += n.textContent;
    }
    var title = head.replace(/[:\s]+$/, '').trim();
    if (!title || /:\s*\S/.test(title)) return null; // "Channels: 3" is a value

    var glued = !!cut;
    var after = nextContent(lead);
    var alone = glued || !after || after.nodeName === 'BR' || isBold(after);
    var caps = title.length > 2 && title === title.toUpperCase() && /[A-Z]/.test(title);
    if (!alone && !caps) return null;

    if (glued) {
      while (lead.firstChild !== cut) lead.firstChild.remove();
      cut.remove();
    } else {
      lead.remove();
    }
    trimLeadingBreaks(block);
    return caps ? title.charAt(0) + title.slice(1).toLowerCase() : title;
  }

  function init(root) {
    var grid = root.querySelector('[data-prov-grid]');
    if (!grid || grid.dataset.lauzonSplit) return;
    grid.dataset.lauzonSplit = '1';

    var body = grid.querySelector('[data-story-body]');
    var column = grid.querySelector('[data-spec-column]');
    var groups = grid.querySelector('[data-spec-groups]');
    if (!body || !column || !groups) return;

    // A bare video-URL paragraph anywhere in the description is per-item
    // video data, not copy — hand it to the gallery and take it out of the
    // page. Runs before the specs split so it also covers the ~108 Woo-era
    // descriptions (nearly all Hall of Fame) that have no split at all and
    // were rendering the URL as raw text.
    Array.prototype.slice.call(body.querySelectorAll('p')).forEach(function (p) {
      var text = p.textContent.trim();
      if (!/^https?:\/\/\S+$/.test(text) || !/youtu\.?be|vimeo/.test(text)) return;
      document.dispatchEvent(new CustomEvent('lauzon:product-video', { detail: { url: text } }));
      var prev = p.previousElementSibling;
      var next = p.nextElementSibling;
      if (prev && prev.tagName === 'HR' && next && next.tagName === 'HR') prev.remove();
      p.remove();
    });

    var specs = body.querySelector('.lz-specs') || specsFromMarker(body);

    // Metafield spec sheet already rendered — just drop the duplicate HTML specs
    if (groups.querySelector('.spec-group')) {
      if (specs) specs.remove();
      return;
    }

    if (!specs) return; // single-panel product; column stays hidden

    // Build an accordion per <p> group. A group heading (see takeHeading)
    // becomes the summary and is removed from the body. Without one, the
    // first data label ("Series:") titles the group and stays visible so no
    // value is orphaned — which is why the description format requires a
    // heading on every group (context/lauzon-product-description-skill.md).
    // Every group starts CLOSED (Dave S., 2026-08-21) — the Body group used to
    // open on every product.
    Array.prototype.slice.call(specs.children).forEach(function (block) {
      var text = block.textContent.trim();
      if (!text) return;

      // A paragraph that is just a video URL is per-item video data, not a
      // spec group — hand it to the gallery ("Watch this exact guitar").
      if (/^https?:\/\/\S+$/.test(text)) {
        if (/youtu\.?be|vimeo/.test(text)) {
          document.dispatchEvent(new CustomEvent('lauzon:product-video', { detail: { url: text } }));
        }
        return;
      }

      var body = block.cloneNode(true);
      var title = takeHeading(body);
      if (!title) {
        // Data label: the lead's first line, up to its colon ("Serial:")
        var lead = firstContent(body);
        var line = '';
        if (isBold(lead)) {
          for (var n = lead.firstChild; n && n.nodeName !== 'BR'; n = n.nextSibling) line += n.textContent;
        }
        title = line.split(':')[0].trim();
      }
      if (!title) {
        // No bold lead: title from a short "Label:" prefix ("Serial: R131233")
        var m = text.match(/^([^:\n]{2,24}):/);
        title = m ? m[1].trim() : 'Details';
      }

      var details = document.createElement('details');
      details.className = 'spec-group';

      var summary = document.createElement('summary');
      summary.textContent = title;
      details.appendChild(summary);

      var content = document.createElement('div');
      content.className = 'spec-group-body';
      content.appendChild(body);
      details.appendChild(content);

      groups.appendChild(details);
    });

    specs.remove();
    column.hidden = false;
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
